import { useState, type FormEvent, type JSX } from 'react';
import { Eye, EyeOff } from 'lucide-react';
import { supabase } from '../lib/supabase';

export default function Login(): JSX.Element | null {
  // Hooks must come before any early return (rules-of-hooks).
  const [email, setEmail] = useState<string>('');
  const [password, setPassword] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [showPassword, setShowPassword] = useState<boolean>(false);
  const [showForgot, setShowForgot] = useState<boolean>(false);
  const [resetEmail, setResetEmail] = useState<string>('');
  const [resetLoading, setResetLoading] = useState<boolean>(false);

  const reason =
    typeof window !== 'undefined'
      ? new URLSearchParams(window.location.search).get('reason')
      : null;
  const reasonNotice =
    reason === 'deactivated'
      ? 'This account has been deactivated. Contact an administrator.'
      : reason === 'idle'
        ? 'Signed out due to inactivity. Sign in again to continue.'
        : null;

  // Password-recovery redirect: Supabase sends the hash with type=recovery;
  // route it to /reset-password after hooks have been declared.
  if (typeof window !== 'undefined' && window.location.hash.includes('type=recovery')) {
    window.location.replace('/reset-password' + window.location.hash);
    return null;
  }

  async function handleSubmit(e: FormEvent<HTMLFormElement>): Promise<void> {
    e.preventDefault();
    setLoading(true);
    setError(null);
    setMessage(null);

    const { error: signInError } = await supabase.auth.signInWithPassword({ email, password });
    if (signInError) {
      setError(signInError.message);
    }
    setLoading(false);
  }

  async function handleForgot(e: FormEvent<HTMLFormElement>): Promise<void> {
    e.preventDefault();
    setError(null);
    setMessage(null);
    setResetLoading(true);

    const { error: resetError } = await supabase.auth.resetPasswordForEmail(resetEmail, {
      redirectTo: window.location.origin + '/reset-password',
    });
    setResetLoading(false);

    if (resetError) {
      setError(resetError.message);
      return;
    }
    setMessage('Check your email for a reset link.');
  }

  return (
    <div className="min-h-screen bg-surface-0 flex items-center justify-center p-4">
      <div className="w-full max-w-sm">
        {/* Logo */}
        <div className="flex items-center gap-3 justify-center mb-8">
          <div className="w-10 h-10 rounded-xl bg-accent flex items-center justify-center">
            <span className="text-white font-bold text-lg">A</span>
          </div>
          <div>
            <div className="text-xl font-semibold text-ink-bright">Arbi</div>
            <div className="text-xs text-ink-muted">Profitability Decision Engine</div>
          </div>
        </div>

        <div className="arbi-card p-6 space-y-4">
          <h1 className="text-base font-semibold text-ink-bright text-center">Sign in</h1>

          {reasonNotice && (
            <div className="p-3 rounded-md bg-warning/10 border border-warning/30 text-xs text-warning">
              {reasonNotice}
            </div>
          )}
          {error && (
            <div className="p-3 rounded-md bg-danger/10 border border-danger/30 text-xs text-danger">
              {error}
            </div>
          )}
          {message && (
            <div className="p-3 rounded-md bg-success/10 border border-success/30 text-xs text-success">
              {message}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-3">
            <div className="space-y-1">
              <label htmlFor="login-email" className="text-xs text-ink-muted">
                Email
              </label>
              <input
                id="login-email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="arbi-input w-full"
                placeholder="you@example.com"
                required
              />
            </div>
            <div className="space-y-1">
              <label htmlFor="login-password" className="text-xs text-ink-muted">
                Password
              </label>
              <div className="relative">
                <input
                  id="login-password"
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="arbi-input w-full pr-9"
                  placeholder="••••••••"
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowPassword((v) => !v)}
                  className="absolute right-2.5 top-1/2 -translate-y-1/2 text-ink-muted hover:text-ink-primary transition-colors"
                  tabIndex={-1}
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                >
                  {showPassword ? <EyeOff size={14} /> : <Eye size={14} />}
                </button>
              </div>
            </div>
            <button
              type="submit"
              disabled={loading}
              className="arbi-btn-primary w-full justify-center py-2 mt-1 disabled:opacity-60"
            >
              {loading ? 'Signing in…' : 'Sign in'}
            </button>
          </form>

          <div className="text-center">
            <button
              type="button"
              onClick={() => setShowForgot((v) => !v)}
              className="text-xs text-accent hover:text-accent-hover"
            >
              Forgot password?
            </button>
          </div>

          {showForgot && (
            <form onSubmit={handleForgot} className="space-y-3 pt-2 border-t border-border">
              <div className="space-y-1">
                <label htmlFor="login-reset-email" className="text-xs text-ink-muted">
                  Email address
                </label>
                <input
                  id="login-reset-email"
                  type="email"
                  value={resetEmail}
                  onChange={(e) => setResetEmail(e.target.value)}
                  className="arbi-input w-full"
                  placeholder="you@example.com"
                  required
                />
              </div>
              <button
                type="submit"
                disabled={resetLoading}
                className="arbi-btn-primary w-full justify-center py-2 disabled:opacity-60"
              >
                {resetLoading ? 'Sending…' : 'Send Reset Email'}
              </button>
            </form>
          )}
        </div>

        <p className="text-center text-xs text-ink-muted mt-4">Access is by invitation only.</p>
        <p className="text-center text-2xs text-ink-muted mt-2">
          Best experienced in Chrome. Firefox, Safari, and Edge are also supported.
        </p>
      </div>
    </div>
  );
}
